/**
 * SECURE STORAGE SERVICE
 * 
 * Handles interaction with Cloudflare R2 via Supabase Edge Functions.
 * Hardened for robust error reporting and CORS compatibility.
 */
import { supabase } from '../supabase/supabase';

const DEFAULT_PUBLIC_URL = 'https://assets.sohlocal.com';

const getBasePublicUrl = () => {
  const envUrl = import.meta.env.VITE_R2_PUBLIC_URL?.trim();
  if (!envUrl) return DEFAULT_PUBLIC_URL;

  try {
    const parsedUrl = new URL(envUrl);
    if (parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:') {
      return envUrl.replace(/\/$/, '');
    }
  } catch {
    return DEFAULT_PUBLIC_URL;
  }

  return DEFAULT_PUBLIC_URL;
};

const PUBLIC_URL = getBasePublicUrl();

const ALLOWED_MIME_TYPES = {
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/gif': 'gif'
};

/**
 * Checks if the secure storage proxy is initialized and reachable.
 */
export const checkR2Status = async () => {
  try {
    const { data, error } = await supabase.functions.invoke('get-upload-url', {
      body: { ping: true }
    });
    
    if (error) {
      console.error("R2 Status Check Error (Supabase Side):", error);
      return { connected: false, message: error.message || "Proxy unreachable" };
    }
    
    if (data?.env_check) {
      const missing = Object.entries(data.env_check).filter(([_, v]) => !v);
      if (missing.length > 0) {
        return { connected: true, message: "Proxy live but missing environment secrets" };
      }
    }
    
    return { connected: true, message: data?.message || "Security Proxy Active" };
  } catch (error) {
    console.error("R2 Status Check Network Failure:", error);
    return { connected: false, message: "Network error or CORS block" };
  }
};

/**
 * Uploads a file to Cloudflare R2 using a pre-signed URL.
 */
export const uploadToR2 = async (file, path) => {
  const mimeType = file.type.toLowerCase();
  if (!ALLOWED_MIME_TYPES[mimeType]) {
    throw new Error("Only JPG, PNG, WEBP, and GIF are allowed.");
  }

  if (file.size > 10 * 1024 * 1024) {
    throw new Error("Images must be 10 MB or smaller.");
  }

  const extension = ALLOWED_MIME_TYPES[mimeType];
  const sanitizedBase = file.name.split('.')[0].replace(/[^a-z0-9]/gi, '-').toLowerCase();
  const fileName = `${Date.now()}-${sanitizedBase}.${extension}`;
  const key = `${path}/${fileName}`.replace(/\/+/g, '/');

  try {
    console.log(`Requesting upload URL for: ${key}`);
    
    // 1. Send the image through the authenticated Edge Function for byte inspection.
    const { data, error } = await supabase.functions.invoke('get-upload-url', {
      body: file,
      headers: {
        'x-storage-action': 'upload',
        'x-storage-key': key,
        'Content-Type': mimeType === 'image/jpg' ? 'image/jpeg' : mimeType
      }
    });

    if (error) {
      console.error("Edge Function Invocation Error:", error);
      // Detailed error breakdown
      if (error.message?.includes('Failed to fetch')) {
        throw new Error("CORS or Network Error: The browser blocked the request to the Edge Function. Check 'x-application-name' in CORS headers.");
      }
      throw new Error(error.message || "Failed to communicate with storage proxy.");
    }

    if (data?.error) throw new Error(data.error);
    if (!data?.success || !data?.publicUrl) {
      throw new Error("Storage server failed to provide a confirmed image URL.");
    }

    console.log("Upload successful!");
    return data.publicUrl;

  } catch (err) {
    console.error("Storage Service Critical Failure:", err);
    throw err;
  }
};

/**
 * Deletes a single file from R2.
 */
export const deleteFromR2 = async (imageUrl) => {
  if (!imageUrl || imageUrl.startsWith('data:')) return true;

  try {
    const key = imageUrl.replace(PUBLIC_URL + '/', '').split('?')[0];
    const { error } = await supabase.functions.invoke('get-upload-url', {
      body: { action: 'delete', key }
    });

    if (error) throw error;
    return true;
  } catch (err) {
    console.error("Failed to delete file from R2:", err);
    return false;
  }
};

/**
 * Administrative cleanup for user folders.
 */
export const deleteUserFolder = async (userId) => {
  try {
    const { error } = await supabase.functions.invoke('get-upload-url', {
      body: { 
        action: 'deleteFolder', 
        prefix: `users/${userId}/` 
      }
    });

    if (error) throw error;
    return true;
  } catch (err) {
    console.error("Failed to cleanup user storage folder:", err);
    return false;
  }
};

export const getBrandingAsset = (assetName) => `${PUBLIC_URL}/Branding/${assetName}`;

export const getSellerPath = (userId, subfolder = '') => 
  `users/${userId}/seller${subfolder ? `/${subfolder}` : ''}`;
